import type { Span, TextError } from './source.js';

export type TokenKind = 'ident' | 'number' | 'string' | 'char' | 'op' | 'eof';

export interface Token {
  kind: TokenKind;
  /** The text as written (for `string` and `char`, the decoded value). */
  text: string;
  line: number;
  column: number;
  from: number;
  to: number;
}

/** Thrown inside the parser and turned into a `TextError` at the top. */
export class LexError extends Error {
  constructor(readonly info: TextError) {
    super(info.code);
  }
}

const OPS = [
  '<<',
  '>>',
  '<=',
  '>=',
  '==',
  '!=',
  '&&',
  '||',
  '++',
  '--',
  '+=',
  '-=',
  '*=',
  '/=',
  '%=',
  '+',
  '-',
  '*',
  '/',
  '%',
  '<',
  '>',
  '=',
  '!',
  '(',
  ')',
  '{',
  '}',
  '[',
  ']',
  ',',
  ';',
  '.',
  '?',
  ':',
  '&',
];

const ID_START = /[A-Za-z_]/;
const ID_PART = /[A-Za-z0-9_]/;
const DIGIT = /[0-9]/;

function fail(
  code: TextError['code'],
  at: Token | Span,
  line: number,
  column: number,
  params?: Record<string, string>,
): never {
  const span = 'from' in at && 'to' in at ? { from: at.from, to: at.to } : { from: 0, to: 0 };
  throw new LexError({ code, line, column, span, ...(params ? { params } : {}) });
}

/**
 * Turns C++ text into tokens. Comments and `#include` lines are skipped: they carry no program,
 * but the student is free to write them.
 */
export function lex(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  let line = 1;
  let lineStart = 0;
  const col = () => i - lineStart + 1;

  const newline = () => {
    line++;
    lineStart = i + 1;
  };

  while (i < source.length) {
    const c = source[i]!;
    if (c === '\n') {
      newline();
      i++;
      continue;
    }
    if (c === ' ' || c === '\t' || c === '\r') {
      i++;
      continue;
    }
    // A preprocessor line (#include <iostream>) belongs to the frame, not to the program.
    if (c === '#' && (i === 0 || source[i - 1] === '\n')) {
      while (i < source.length && source[i] !== '\n') i++;
      continue;
    }
    if (c === '/' && source[i + 1] === '/') {
      while (i < source.length && source[i] !== '\n') i++;
      continue;
    }
    if (c === '/' && source[i + 1] === '*') {
      const startLine = line;
      const startCol = col();
      const from = i;
      i += 2;
      while (i < source.length && !(source[i] === '*' && source[i + 1] === '/')) {
        if (source[i] === '\n') newline();
        i++;
      }
      if (i >= source.length) {
        fail('unterminated', { from, to: source.length }, startLine, startCol, { what: 'comment' });
      }
      i += 2;
      continue;
    }
    const from = i;
    const startCol = col();
    if (DIGIT.test(c)) {
      while (i < source.length && DIGIT.test(source[i]!)) i++;
      if (source[i] === '.' && DIGIT.test(source[i + 1] ?? '')) {
        i++;
        while (i < source.length && DIGIT.test(source[i]!)) i++;
      }
      tokens.push({
        kind: 'number',
        text: source.slice(from, i),
        line,
        column: startCol,
        from,
        to: i,
      });
      continue;
    }
    if (ID_START.test(c)) {
      while (i < source.length && ID_PART.test(source[i]!)) i++;
      tokens.push({
        kind: 'ident',
        text: source.slice(from, i),
        line,
        column: startCol,
        from,
        to: i,
      });
      continue;
    }
    if (c === '"') {
      i++;
      let value = '';
      while (i < source.length && source[i] !== '"') {
        if (source[i] === '\n')
          fail('unterminated', { from, to: i }, line, startCol, { what: 'text' });
        if (source[i] === '\\') {
          const esc = source[i + 1];
          value +=
            esc === 'n'
              ? '\n'
              : esc === 't'
                ? '\t'
                : esc === '\\'
                  ? '\\'
                  : esc === '"'
                    ? '"'
                    : (esc ?? '');
          i += 2;
          continue;
        }
        value += source[i];
        i++;
      }
      if (i >= source.length)
        fail('unterminated', { from, to: i }, line, startCol, { what: 'text' });
      i++;
      tokens.push({ kind: 'string', text: value, line, column: startCol, from, to: i });
      continue;
    }
    if (c === "'") {
      // A character literal: 'a', '\n', '\''.
      i++;
      let value = '';
      if (source[i] === '\\') {
        const esc = source[i + 1];
        value = esc === 'n' ? '\n' : esc === 't' ? '\t' : esc === '0' ? '\0' : (esc ?? '');
        i += 2;
      } else if (i < source.length && source[i] !== '\n' && source[i] !== "'") {
        value = source[i]!;
        i++;
      }
      if (source[i] !== "'" || value === '') {
        fail('unterminated', { from, to: i }, line, startCol, { what: 'character' });
      }
      i++;
      tokens.push({ kind: 'char', text: value, line, column: startCol, from, to: i });
      continue;
    }
    const op = OPS.find((o) => source.startsWith(o, i));
    if (!op) {
      i++;
      fail('expected', { from, to: i }, line, startCol, { found: c });
    }
    i += op.length;
    tokens.push({ kind: 'op', text: op, line, column: startCol, from, to: i });
  }
  tokens.push({
    kind: 'eof',
    text: '',
    line,
    column: col(),
    from: source.length,
    to: source.length,
  });
  return tokens;
}
